/**
 * NodePanel — Shura Nexus drill-in side panel (Palantir-deep edition).
 *
 * Intelligence-brief vibe: bracket headers, monospace metrics, SVG ring
 * for the composite Shura Score, dotted dividers between data rows, and
 * a bright violet pulse on the active node accent.
 */
import React, { useEffect, useState } from 'react';
import { X, ShieldAlert, ShieldCheck, ShieldQuestion } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ScoreRing } from './ScoreRing';

export function NodePanel({ nodeId, onClose, isDark }) {
  const [detail, setDetail] = useState(null);
  const [risk, setRisk] = useState(null);
  const [riskOpen, setRiskOpen] = useState(false);

  /* eslint-disable -- async fetch with cleanup is the standard pattern; v6 rule false-positive */
  useEffect(() => {
    if (!nodeId) return undefined;
    let cancelled = false;
    setRisk(null);
    setRiskOpen(false);
    api.get(`/shura/node/${encodeURIComponent(nodeId)}`)
      .then(({ data }) => { if (!cancelled) setDetail(data); })
      .catch(() => { if (!cancelled) toast.error('Failed to load node detail'); });
    return () => { cancelled = true; };
  }, [nodeId]);
  /* eslint-enable */

  const loadRiskPulse = () => {
    setRiskOpen(true);
    if (risk) return;
    api.get(`/insights/risk-pulse/${encodeURIComponent(nodeId)}`)
      .then(({ data }) => setRisk(data))
      .catch(() => toast.error('Risk pulse unavailable.'));
  };

  const detailForCurrent = detail && detail.node && detail.node.id === nodeId ? detail : null;
  const loading = nodeId && !detailForCurrent;
  const titleCls = isDark ? 'text-white' : 'text-[#0A0A0A]';

  if (!nodeId) return null;
  if (loading) return (
    <div className="absolute right-4 top-4 w-[360px] pal-card pal-card-live pal-grid-bg p-5 z-20">
      <div className="pal-bracket">[NODE] LOADING…</div>
      <div className="mt-3 text-xs opacity-60 pal-mono">▓▓▓▓▒▒░░░░░░░░░░</div>
    </div>
  );
  if (!detailForCurrent) return null;
  const { node, score, neighbours } = detailForCurrent;

  return (
    <div
      className="absolute right-4 top-4 w-[380px] max-h-[calc(100%-2rem)] overflow-y-auto pal-card pal-card-live pal-grid-bg z-20"
      data-testid="shura-node-panel"
    >
      {/* Header */}
      <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-[rgba(174,82,234,0.15)]">
        <div className="min-w-0">
          <div className="pal-bracket truncate">
            [{node.kind.replace('_', ' ').toUpperCase()}]
          </div>
          <div className={`text-base font-semibold mt-1 truncate ${titleCls}`}>
            {node.label}
          </div>
          <div className="pal-mono text-[10px] opacity-50 mt-1 truncate">
            ID · {String(node.id).slice(0, 28)}{String(node.id).length > 28 ? '…' : ''}
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="shura-node-panel-close"
          className={`p-1 rounded ${isDark ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-slate-500 hover:text-black hover:bg-slate-100'}`}
        >
          <X size={15} />
        </button>
      </div>

      {/* Shura Score ring */}
      <div className="px-5 pt-4">
        <div className="flex items-center justify-between mb-2">
          <span className="pal-bracket">[SHURA SCORE]</span>
          <span className={`pal-status ${
            score.composite >= 75 ? 'pal-status-violet'
            : score.composite >= 50 ? 'pal-status-ok'
            : score.composite >= 25 ? 'pal-status-warn'
            : 'pal-status-alert'
          }`}>
            {score.composite >= 75 ? 'PRIME'
            : score.composite >= 50 ? 'SOLID'
            : score.composite >= 25 ? 'EMERGING'
            : 'WEAK'}
          </span>
        </div>
        <ScoreRing
          composite={score.composite}
          dimensions={{
            influence: score.influence,
            trust: score.trust,
            innovation: score.innovation,
            investment: score.investment,
            collaboration: score.collaboration,
          }}
          isDark={isDark}
          size={240}
        />
      </div>

      {/* Tabular readout below the ring — Bloomberg-style label : value rows */}
      <div className="px-5 pt-2 pb-4">
        <div className="pal-row"><span className="pal-key">INFLUENCE</span>
          <ProgressInline value={score.influence} color="#AE52EA" />
        </div>
        <div className="pal-row"><span className="pal-key">TRUST</span>
          <ProgressInline value={score.trust} color="#3B82F6" />
        </div>
        <div className="pal-row"><span className="pal-key">INNOVATION</span>
          <ProgressInline value={score.innovation} color="#7C3AED" />
        </div>
        <div className="pal-row"><span className="pal-key">INVESTMENT</span>
          <ProgressInline value={score.investment} color="#10B981" />
        </div>
        <div className="pal-row"><span className="pal-key">COLLABORATION</span>
          <ProgressInline value={score.collaboration} color="#06B6D4" />
        </div>
      </div>

      {/* Neighbours */}
      <div className="px-5 pb-4 border-t border-[rgba(174,82,234,0.15)] pt-4">
        <div className="flex items-center justify-between mb-2">
          <span className="pal-bracket">[DIRECT EDGES]</span>
          <span className="pal-mono text-[10px] opacity-60">N={neighbours.length}</span>
        </div>
        <div className="space-y-1 max-h-[180px] overflow-y-auto pr-1">
          {neighbours.length === 0 ? (
            <div className="text-xs opacity-60 italic">No direct connections yet.</div>
          ) : neighbours.map((nb, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded ${
                isDark ? 'hover:bg-white/5 text-slate-200' : 'hover:bg-slate-100/70 text-slate-700'
              }`}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: nb.edge_color, boxShadow: `0 0 6px ${nb.edge_color}` }} />
              <span className="flex-1 truncate pal-mono text-[11px]">{nb.node.label}</span>
              <span className="pal-mono text-[9px] opacity-50 uppercase">
                {nb.edge_kind.replace('_', ' ')}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Risk Pulse — Phase 5 */}
      <div className="px-5 pb-5 border-t border-[rgba(174,82,234,0.15)] pt-4">
        {!riskOpen ? (
          <button
            onClick={loadRiskPulse}
            data-testid="shura-risk-pulse-load"
            className={`w-full text-left p-3 rounded-md border transition-colors flex items-center gap-2 ${
              isDark
                ? 'bg-white/[0.02] border-white/10 hover:border-[#AE52EA]/40 hover:bg-white/5'
                : 'bg-slate-50 border-slate-200 hover:border-[#5E1F8B]/40'
            }`}
          >
            <ShieldQuestion size={14} className="text-[var(--ve-violet-bright)]" />
            <span className="pal-bracket">[RISK PULSE]</span>
            <span className="pal-mono text-[10px] ml-auto opacity-60">CLICK TO COMPUTE →</span>
          </button>
        ) : (
          <RiskPulseCard risk={risk} isDark={isDark} />
        )}
      </div>
    </div>
  );
}

/* ───── Inline ASCII-style progress used in tabular readouts ─────────── */
function ProgressInline({ value, color }) {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  const filledCount = Math.round((v / 100) * 12);
  return (
    <div className="flex items-center gap-2">
      <div className="pal-progress" aria-hidden>
        <span className="pal-fill" style={{ color }}>{'█'.repeat(filledCount)}</span>
        <span className="pal-empty">{'░'.repeat(12 - filledCount)}</span>
      </div>
      <span className="pal-val text-[11px] tabular-nums w-8 text-right">{v.toFixed(0)}</span>
    </div>
  );
}

function RiskPulseCard({ risk, isDark }) {
  if (!risk) {
    return (
      <div className="p-3 rounded-md border border-[rgba(174,82,234,0.18)] bg-[rgba(174,82,234,0.04)]" data-testid="shura-risk-pulse-loading">
        <span className="pal-bracket">[RISK PULSE]</span>
        <div className="pal-mono text-[10px] mt-2 opacity-70">Yusuf computing… ▓▒░░░░░░░░░░░░░░░</div>
      </div>
    );
  }
  const bandColor = risk.band === 'low' ? '#10B981' : risk.band === 'moderate' ? '#F59E0B' : '#EF4444';
  const Icon = risk.band === 'low' ? ShieldCheck : ShieldAlert;
  const dimensions = [
    { key: 'governance',    label: 'GOV ' },
    { key: 'liquidity',     label: 'LIQ ' },
    { key: 'concentration', label: 'CONC' },
    { key: 'stagnation',    label: 'STAG' },
    { key: 'reputation',    label: 'REP ' },
  ];
  return (
    <div className="rounded-md border border-[rgba(174,82,234,0.20)] bg-[rgba(10,5,16,0.4)] p-3" data-testid="shura-risk-pulse-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={13} style={{ color: bandColor }} />
          <span className="pal-bracket">[RISK · YUSUF]</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`pal-status pal-status-${risk.band === 'low' ? 'ok' : risk.band === 'moderate' ? 'warn' : 'alert'}`}>
            {risk.band?.toUpperCase()}
          </span>
          <span className="pal-mono text-base font-bold tabular-nums" style={{ color: bandColor }}>{risk.composite}</span>
        </div>
      </div>
      {dimensions.map((d) => {
        const val = risk.dimensions[d.key];
        const c = val > 60 ? '#EF4444' : val > 30 ? '#F59E0B' : '#10B981';
        return (
          <div key={d.key} className="pal-row">
            <span className="pal-key pal-mono">{d.label}</span>
            <ProgressInline value={val} color={c} />
          </div>
        );
      })}
      <div className={`mt-3 pt-3 border-t border-dashed border-[rgba(174,82,234,0.18)] text-[11px] italic leading-relaxed ${
        isDark ? 'text-slate-300' : 'text-slate-700'
      }`}>
        “{risk.narrative}”
      </div>
    </div>
  );
}

export function StatPill({ value, label, isDark }) {
  return (
    <div className={`px-3 py-1.5 rounded-md border ${
      isDark ? 'bg-white/[0.04] border-white/10' : 'bg-white border-slate-200'
    }`}>
      <div className={`pal-mono text-sm tabular-nums font-bold ${isDark ? 'text-white' : 'text-[#0A0A0A]'}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-[0.18em] mt-0.5 opacity-60">{label}</div>
    </div>
  );
}
